import React, { useEffect, useState, useCallback, useMemo } from "react";
import baseUrl from "../../../../api/baseUrl";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import BadgeIcon from "@mui/icons-material/Badge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import getDecryptedToken from "../../../../api/getDecryptedToken";
import { toast } from "react-toastify";
import { colorOptions } from "../addemployee/employeeFormShared";

function formatEmployeeSummaryDate(raw: unknown): string {
  if (raw == null || raw === "") return "—";
  let d: Date;
  if (raw instanceof Date) {
    d = raw;
  } else if (typeof raw === "number" && Number.isFinite(raw)) {
    d = new Date(raw < 1e12 ? raw * 1000 : raw);
  } else if (typeof raw === "string") {
    d = new Date(raw);
  } else {
    return "—";
  }
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return d.toLocaleString();
  }
}

const Employees: React.FC = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [deactivated, setDeactivated] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appointmentAppLimit, setAppointmentAppLimit] = useState<number | null>(null);
  const [appointmentAppUsed, setAppointmentAppUsed] = useState<number>(0);
  const [togglingAppointmentId, setTogglingAppointmentId] = useState<string | null>(null);
  const [appointmentApp, setAppointmentApp] = useState<boolean>(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<Record<string, unknown> | null>(null);
  const navigate = useNavigate();
  const roleName = getDecryptedToken("role");
  const isSuperAdmin = roleName === "Super Admin";

  const refreshAppointmentQuota = useCallback(async () => {
    try {
      const res = await baseUrl.get("/employee/appointmentAppQuota");
      console.log(res.data);

      if (res.data?.success && res.data?.data) {
        setAppointmentAppLimit(res.data.data.limit);
        setAppointmentAppUsed(res.data.data.used);
      }
    } catch (error) {
      /* ignore */
      console.log(error);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [empRes, quotaRes] = await Promise.all([
          baseUrl.get("/employee/findEmployee", { headers: { status: true } }),
          baseUrl.get("/employee/appointmentAppQuota"),
        ]);
        setTableData(empRes.data.data);
        setFilteredData(empRes.data.data);
        if (quotaRes.data?.success && quotaRes.data?.data) {
          setAppointmentAppLimit(quotaRes.data.data.limit);
          setAppointmentAppUsed(quotaRes.data.data.used);
          setAppointmentApp(quotaRes.data.appointmentApp ?? false);

        }
      } catch (error) {
        console.log(error);
      }
    })();
  }, [deactivated]);

  const handleToggleAppointmentApp = useCallback(
    async (item: any, enabled: boolean) => {
      if (!isSuperAdmin) return;
      if (enabled && appointmentAppLimit != null && !item.hasAppointmentApp) {
        if (appointmentAppUsed >= appointmentAppLimit) {
          toast.error(
            `Appointment app access is limited to ${appointmentAppLimit} staff member(s). Remove access from someone else or ask an administrator to raise the limit.`
          );
          return;
        }
      }
      setTogglingAppointmentId(item._id);
      try {
        await baseUrl.patch(
          "/employee/hasAppointmentApp",
          { hasAppointmentApp: enabled },
          { headers: { employeeid: item._id } }
        );
        setTableData((prev) =>
          prev.map((e) => (e._id === item._id ? { ...e, hasAppointmentApp: enabled } : e))
        );
        await refreshAppointmentQuota();
        toast.success(enabled ? "Appointment app access enabled" : "Appointment app access disabled");
      } catch (err: any) {
        const msg = err?.response?.data?.message || "Could not update appointment app access.";
        toast.error(msg);
      } finally {
        setTogglingAppointmentId(null);
      }
    },
    [isSuperAdmin, appointmentAppLimit, appointmentAppUsed, refreshAppointmentQuota]
  );
  useEffect(() => {
    const filtered = tableData.filter(
      (item) =>
        item?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.roleId?.roleName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, tableData]);

  const deactivateEmployee = useCallback(
    (employeeId: string) => {
      const employee = tableData.find((e) => e._id === employeeId);
      if (employee?.roleId?.roleName === "Super Admin") {
        const superAdminCount = tableData.filter(
          (e) => e?.roleId?.roleName === "Super Admin"
        ).length;
        if (superAdminCount <= 1) {
          toast.error(
            "Cannot deactivate the last Super Admin. Each clinic must have at least one active Super Admin."
          );
          return;
        }
      }

      if (window.confirm("Are you sure you want to deactivate this employee?")) {
        baseUrl
          .patch(
            "/employee/deactivateEmployee",
            {},
            {
              headers: {
                employeeid: employeeId,
              },
            }
          )
          .then(() => {
            setDeactivated((prev) => !prev);
            toast.success("Employee deactivated");
          })
          .catch((error: { response?: { data?: { message?: string } } }) => {
            toast.error(
              error?.response?.data?.message || "Could not deactivate employee."
            );
          });
      }
    },
    [tableData]
  );

  const handleEditClick = useCallback(
    (id: string) => {
      navigate(`/client/staff/employee/edit-employee/${id}`);
    },
    [navigate]
  );

  const openEmployeeSummary = useCallback(async (employeeDocId: string) => {
    setSummaryOpen(true);
    setSummaryLoading(true);
    setSummaryData(null);
    try {
      const res = await baseUrl.get("/employee/findEmployeeById", {
        headers: { employeeid: employeeDocId },
      });
      setSummaryData(res.data?.data ?? null);
    } catch {
      toast.error("Could not load employee details.");
      setSummaryOpen(false);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const closeEmployeeSummary = useCallback(() => {
    setSummaryOpen(false);
    setSummaryData(null);
  }, []);

  // Mobile Card View
  const mobileCards = useMemo(
    () =>
      filteredData.map((item) => (
        <div
          key={item._id}
          className="bg-white dark:bg-slate-800 rounded-lg p-3 shadow-md border border-slate-100 dark:border-slate-700"
        >
          {/* Header with Avatar and Name */}
          <div className="flex items-start justify-between mb-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center text-white font-semibold shadow-md text-xs">
                {item?.firstName?.charAt(0)}
                {item?.lastName?.charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-xs text-slate-800 dark:text-slate-100">
                  {item?.firstName + " " + item?.lastName}
                </p>
                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800">
                  {item?.roleId?.roleName}
                </span>
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => void openEmployeeSummary(item._id)}
                className="p-1.5 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                title="View summary"
                aria-label="View employee summary"
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </button>
              {isSuperAdmin && (
                <button
                  onClick={() => handleEditClick(item._id)}
                  className="p-1.5 rounded-md bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                >
                  <BorderColorIcon sx={{ fontSize: 16 }} />
                </button>
              )}
              {isSuperAdmin && (
                <button
                  onClick={() => deactivateEmployee(item._id)}
                  className="p-1.5 rounded-md bg-red-50 dark:bg-red-900/40 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors"
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </button>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <EmailIcon sx={{ fontSize: 14 }} className="text-slate-400" />
              <span className="truncate">{item?.email}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <BadgeIcon sx={{ fontSize: 14 }} className="text-slate-400" />
              <span>{item?.employeeId}</span>
            </div>
          </div>

          {/* Status Badges */}
          <div className="flex gap-2 mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-600">
            <div className="flex-1">
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-0.5">Dentist</p>
              {item?.IsDentist ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300">
                  <CheckCircleIcon sx={{ fontSize: 10 }} />
                  Yes
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                  <CancelIcon sx={{ fontSize: 10 }} />
                  No
                </span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-0.5">Book Dentist</p>
              {item?.IsBookDentist ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300">
                  <CheckCircleIcon sx={{ fontSize: 10 }} />
                  Yes
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                  <CancelIcon sx={{ fontSize: 10 }} />
                  No
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mb-0.5">Appointment app</p>
              {isSuperAdmin ? (
                <label
                  className={`inline-flex items-center gap-2 ${togglingAppointmentId === item._id ? "opacity-60" : "cursor-pointer"}`}
                >
                  <div className="relative shrink-0">
                    <input
                      type="checkbox"
                      checked={!!item.hasAppointmentApp}
                      disabled={togglingAppointmentId === item._id}
                      onChange={(e) => {
                        void handleToggleAppointmentApp(item, e.target.checked);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-300 dark:bg-slate-600 rounded-full peer-checked:bg-primary transition-colors peer-disabled:opacity-50" />
                    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white dark:bg-slate-200 rounded-full shadow-sm transition-transform peer-checked:translate-x-4 peer-disabled:opacity-50" />
                  </div>
                  <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">
                    {item.hasAppointmentApp ? "On" : "Off"}
                  </span>
                </label>
              ) : item?.hasAppointmentApp ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300">
                  <CheckCircleIcon sx={{ fontSize: 10 }} />
                  Yes
                </span>
              ) : (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                  <CancelIcon sx={{ fontSize: 10 }} />
                  No
                </span>
              )}
            </div>
          </div>
        </div>
      )),
    [
      filteredData,
      handleEditClick,
      deactivateEmployee,
      isSuperAdmin,
      handleToggleAppointmentApp,
      togglingAppointmentId,
      openEmployeeSummary,
    ]
  );

  // Desktop Table Rows
  const tableRows = useMemo(
    () =>
      filteredData.map((item, index) => (
        <tr
          key={item._id}
          className="border-b border-slate-100 dark:border-slate-600 hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors"
        >
          <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
            {index + 1}
          </td>
          <td className="px-3 py-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center text-white font-semibold text-xs shadow-md">
                {item?.firstName?.charAt(0)}
                {item?.lastName?.charAt(0)}
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                  {item?.firstName + " " + item?.lastName}
                </p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500">{item?.employeeId}</p>
              </div>
            </div>
          </td>
          <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300 normal-case">{item?.email}</td>
          <td className="px-3 py-2">
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-md bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800">
              {item?.roleId?.roleName}
            </span>
          </td>
          <td className="px-3 py-2">
            {item?.IsDentist ? (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded-md bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300">
                <CheckCircleIcon sx={{ fontSize: 12 }} />
                Yes
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                <CancelIcon sx={{ fontSize: 12 }} />
                No
              </span>
            )}
          </td>
          <td className="px-3 py-2">
            {item?.IsBookDentist ? (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded-md bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300">
                <CheckCircleIcon sx={{ fontSize: 12 }} />
                Yes
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                <CancelIcon sx={{ fontSize: 12 }} />
                No
              </span>
            )}
          </td>
          {appointmentApp && appointmentAppLimit != null && appointmentAppLimit > 0 && <td className="px-3 py-2">
            {isSuperAdmin ? (
              <label
                className={`inline-flex items-center gap-2 ${togglingAppointmentId === item._id ? "opacity-60" : "cursor-pointer"}`}
              >
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={!!item.hasAppointmentApp}
                    disabled={togglingAppointmentId === item._id}
                    onChange={(e) => {
                      void handleToggleAppointmentApp(item, e.target.checked);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-slate-300 dark:bg-slate-600 rounded-full peer-checked:bg-primary transition-colors peer-disabled:opacity-50" />
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white dark:bg-slate-200 rounded-full shadow-sm transition-transform peer-checked:translate-x-4 peer-disabled:opacity-50" />
                </div>
                <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                  {item.hasAppointmentApp ? "On" : "Off"}
                </span>
              </label>
            ) : item?.hasAppointmentApp ? (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded-md bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300">
                <CheckCircleIcon sx={{ fontSize: 12 }} />
                Yes
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-400">
                <CancelIcon sx={{ fontSize: 12 }} />
                No
              </span>
            )}
          </td>}
          <td className="px-3 py-2">
            <button
              type="button"
              onClick={() => void openEmployeeSummary(item._id)}
              className="p-1.5 rounded-md bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
              title="View summary"
              aria-label="View employee summary"
            >
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </button>
          </td>
          <td className="px-3 py-2">
            {isSuperAdmin ? (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => handleEditClick(item._id)}
                  className="p-1.5 rounded-md bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                  title="Edit"
                >
                  <BorderColorIcon sx={{ fontSize: 16 }} />
                </button>
                <button
                  onClick={() => deactivateEmployee(item._id)}
                  className="p-1.5 rounded-md bg-red-50 dark:bg-red-900/40 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors"
                  title="Delete"
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-500">No actions available</span>
            )}
          </td>
        </tr>
      )),
    [
      filteredData,
      handleEditClick,
      deactivateEmployee,
      isSuperAdmin,
      handleToggleAppointmentApp,
      togglingAppointmentId,
      appointmentApp,
      appointmentAppLimit,
      openEmployeeSummary,
    ]
  );

  const summaryRoleName =
    summaryData &&
    typeof summaryData.roleId === "object" &&
    summaryData.roleId !== null &&
    "roleName" in summaryData.roleId
      ? String((summaryData.roleId as { roleName?: string }).roleName ?? "")
      : "";

  const formatList = (v: unknown) =>
    Array.isArray(v) ? v.map((x) => String(x)).filter(Boolean).join(", ") : v != null ? String(v) : "—";

  const calendarColorStr =
    typeof summaryData?.color === "string" && summaryData.color.trim() ? summaryData.color.trim() : "";
  const calendarColorLabel = colorOptions.find((c) => c.color === calendarColorStr)?.name;

  return (
    <div className="  p-3 md:p-0">
      <div className=" mx-auto">
        {/* Header */}


        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg md:rounded-xl p-2.5 md:p-4 shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700">
            <div className="flex flex-col md:flex-row items-center gap-1.5 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-50 rounded-md md:rounded-lg flex items-center justify-center">
                <PeopleAltIcon className="text-indigo-500" sx={{ fontSize: { xs: 16, md: 20 } }} />
              </div>
              <div className="text-center md:text-left">
                <p className="text-base md:text-lg font-bold text-slate-800 dark:text-slate-100">{tableData.length}</p>
                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg md:rounded-xl p-2.5 md:p-4 shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700">
            <div className="flex flex-col md:flex-row items-center gap-1.5 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-emerald-50 rounded-md md:rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="text-emerald-500" sx={{ fontSize: { xs: 16, md: 20 } }} />
              </div>
              <div className="text-center md:text-left">
                <p className="text-base md:text-lg font-bold text-slate-800 dark:text-slate-100">
                  {tableData.filter((e) => e.IsDentist).length}
                </p>
                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400">Dentists</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg md:rounded-xl p-2.5 md:p-4 shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700">
            <div className="flex flex-col md:flex-row items-center gap-1.5 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-50 rounded-md md:rounded-lg flex items-center justify-center">
                <CheckCircleIcon className="text-purple-500" sx={{ fontSize: { xs: 16, md: 20 } }} />
              </div>
              <div className="text-center md:text-left">
                <p className="text-base md:text-lg font-bold text-slate-800 dark:text-slate-100">
                  {tableData.filter((e) => e.IsBookDentist).length}
                </p>
                <p className="text-[10px] md:text-xs text-slate-500 dark:text-slate-400">Booking</p>
              </div>
            </div>
          </div>

        </div>

        {/* Search Bar */}
        <div className="mb-2 md:mb-0 md:hidden">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all shadow-sm text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
            />
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2">
          {mobileCards}
          {filteredData.length === 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-lg py-8 text-center shadow-md border border-slate-100 dark:border-slate-700">
              <PeopleAltIcon className="text-slate-300 mx-auto mb-3" sx={{ fontSize: 36 }} />
              <p className="text-slate-500 dark:text-slate-400 font-medium text-xs">No employees found</p>
              <p className="text-slate-400 dark:text-slate-500 text-[10px] mt-1">
                {searchTerm ? "Try adjusting your search" : "Add your first employee"}
              </p>
            </div>
          )}
          <div className="text-center py-1.5">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Showing <span className="font-semibold">{filteredData.length}</span> of{" "}
              <span className="font-semibold">{tableData.length}</span>
            </p>
          </div>
        </div>

        {/* Desktop Table Card */}
        <div className="hidden md:block bg-white dark:bg-slate-800 rounded-[8px] shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
          {/* Search Bar */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-600">
            <div className="relative max-w-md">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" sx={{ fontSize: 16 }} />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    Dentist
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    Book Dentist
                  </th>
                  {appointmentApp && appointmentAppLimit != null && appointmentAppLimit > 0 && <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    Appointment App
                  </th>}
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider w-14">
                    View
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {tableRows}
              </tbody>
            </table>

            {/* Empty State */}
            {filteredData.length === 0 && (
              <div className="py-12 text-center">
                <PeopleAltIcon className="text-slate-300 mx-auto mb-3" sx={{ fontSize: 48 }} />
                <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">No employees found</p>
                <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">
                  {searchTerm ? "Try adjusting your search" : "Add your first employee to get started"}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-600">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Showing <span className="font-semibold text-slate-700 dark:text-slate-200">{filteredData.length}</span> of{" "}
              <span className="font-semibold text-slate-700 dark:text-slate-200">{tableData.length}</span> employees
            </p>
            {isSuperAdmin && appointmentAppLimit != null && (
              <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                Appointment app access: {appointmentAppUsed} of {appointmentAppLimit} slot
                {appointmentAppLimit === 1 ? "" : "s"} used (clinic document limit)
              </p>
            )}
          </div>
        </div>
      </div>

      {summaryOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-5 bg-slate-950/50 backdrop-blur-[2px]"
          role="presentation"
          onClick={closeEmployeeSummary}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-summary-title"
            aria-describedby="employee-summary-desc"
            className="relative flex w-full max-w-xl max-h-[90vh] flex-col overflow-hidden rounded-xl bg-white shadow-xl shadow-slate-900/15 ring-1 ring-slate-200/90 dark:bg-slate-900 dark:shadow-black/30 dark:ring-slate-700/80"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="relative shrink-0 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white px-4 py-3 dark:border-slate-800 dark:from-slate-800/80 dark:to-slate-900 sm:px-5">
              <p id="employee-summary-desc" className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
                Staff profile
              </p>
              <h2
                id="employee-summary-title"
                className="mt-0.5 text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg pr-10"
              >
                Employee overview
              </h2>
              <button
                type="button"
                onClick={closeEmployeeSummary}
                className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-4">
              {summaryLoading && (
                <div className="flex flex-col items-center justify-center gap-3 py-14">
                  <div
                    className="h-8 w-8 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-primary dark:border-slate-600 dark:border-t-primary"
                    aria-hidden
                  />
                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Loading profile…</p>
                </div>
              )}

              {!summaryLoading && summaryData && (
                <div className="space-y-4">
                  {(() => {
                    const emailRaw = summaryData.email;
                    const phoneRaw = summaryData.phoneNumber;
                    const email =
                      typeof emailRaw === "string" && emailRaw.trim() ? emailRaw.trim() : "";
                    const phone =
                      typeof phoneRaw === "string" && phoneRaw.trim() ? phoneRaw.trim() : "";
                    const telDigits = phone.replace(/[^\d+]/g, "");
                    const telHref = telDigits.length > 0 ? `tel:${telDigits}` : "";
                    const hasContact = !!(email || phone);

                    const fullDisplayName =
                      `${typeof summaryData.title === "string" && summaryData.title.trim() ? `${summaryData.title.trim()} ` : ""}${[summaryData.firstName, summaryData.lastName]
                        .map((p) => (typeof p === "string" ? p.trim() : ""))
                        .filter(Boolean)
                        .join(" ")}`.trim() || "—";

                    const hasEmployeeId =
                      typeof summaryData.employeeId === "string" && !!summaryData.employeeId.trim();

                    const hasRightMeta = hasContact || hasEmployeeId;

                    const linkRowClass =
                      "group flex w-full max-w-sm items-center justify-center gap-2 rounded-md py-1 text-left transition-colors hover:bg-slate-100/90 dark:hover:bg-slate-800/70 sm:max-w-none sm:justify-start";

                    const metaRowClass =
                      "flex w-full max-w-sm items-center justify-center gap-2 rounded-md py-1 text-left sm:max-w-none sm:justify-start";

                    const rightMetaBlock = hasRightMeta ? (
                      <div className="flex w-full flex-col items-center gap-0.5 sm:min-w-0 sm:max-w-none sm:flex-1 sm:items-stretch sm:border-l sm:border-slate-200/60 sm:pl-3 dark:sm:border-slate-700/50">
                        {email ? (
                          <a href={`mailto:${email}`} className={linkRowClass}>
                            <EmailIcon
                              className="shrink-0 text-slate-400 transition-colors group-hover:text-primary dark:text-slate-500"
                              sx={{ fontSize: 18 }}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700 transition-colors group-hover:text-primary dark:text-slate-200 dark:group-hover:text-primaryLight">
                              {email}
                            </span>
                          </a>
                        ) : null}
                        {phone ? (
                          telHref ? (
                            <a href={telHref} className={linkRowClass}>
                              <LocalPhoneIcon
                                className="shrink-0 text-slate-400 transition-colors group-hover:text-primary dark:text-slate-500"
                                sx={{ fontSize: 18 }}
                                aria-hidden
                              />
                              <span className="min-w-0 flex-1 break-words text-sm font-medium text-slate-700 transition-colors group-hover:text-primary dark:text-slate-200 dark:group-hover:text-primaryLight">
                                {phone}
                              </span>
                            </a>
                          ) : (
                            <div className={metaRowClass}>
                              <LocalPhoneIcon
                                className="shrink-0 text-slate-400 dark:text-slate-500"
                                sx={{ fontSize: 18 }}
                                aria-hidden
                              />
                              <span className="min-w-0 flex-1 break-words text-sm font-medium text-slate-700 dark:text-slate-200">
                                {phone}
                              </span>
                            </div>
                          )
                        ) : null}
                        {hasEmployeeId ? (
                          <div className={metaRowClass}>
                            <BadgeIcon
                              className="shrink-0 text-slate-400 dark:text-slate-500"
                              sx={{ fontSize: 18 }}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1 font-mono text-sm font-medium tracking-wide text-slate-700 dark:text-slate-200">
                              ID {String(summaryData.employeeId).trim()}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    ) : null;

                    return (
                      <>
                        <div className="rounded-xl border border-slate-100/90 bg-gradient-to-b from-slate-50/90 to-white p-4 shadow-sm dark:border-slate-800 dark:from-slate-800/35 dark:to-slate-900/40 sm:p-4">
                          <div className="border-b border-slate-100 pb-3 dark:border-slate-800">
                            <div className="flex w-full flex-row items-center justify-between gap-3">
                              <h3 className="min-w-0 flex-1 text-left text-xl font-semibold leading-tight tracking-tight text-slate-900 text-pretty dark:text-slate-50 sm:text-2xl">
                                {fullDisplayName}
                              </h3>
                              {summaryRoleName ? (
                                <span className="inline-flex shrink-0 items-center rounded-full border border-indigo-100/90 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700 dark:border-indigo-800/60 dark:bg-indigo-950/55 dark:text-indigo-200">
                                  {summaryRoleName}
                                </span>
                              ) : null}
                            </div>
                          </div>

                          <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:items-start sm:gap-3">
                            {(typeof summaryData.imageUrl === "string" && summaryData.imageUrl) ||
                            (typeof summaryData.image === "string" && summaryData.image) ? (
                              <div className="relative shrink-0">
                                <img
                                  src={String(summaryData.imageUrl || summaryData.image)}
                                  alt=""
                                  className="h-20 w-20 rounded-xl object-cover shadow-sm ring-1 ring-slate-200/80 dark:ring-slate-700 sm:h-24 sm:w-24"
                                />
                              </div>
                            ) : (
                              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primaryLight text-base font-semibold text-white shadow-sm ring-1 ring-slate-200/80 dark:ring-slate-700 sm:h-24 sm:w-24 sm:text-lg">
                                {String(summaryData.firstName ?? "").charAt(0)}
                                {String(summaryData.lastName ?? "").charAt(0)}
                              </div>
                            )}

                            {rightMetaBlock ? (
                              <div className="flex min-w-0 w-full flex-1 flex-col sm:flex-row sm:items-start sm:justify-start">
                                {rightMetaBlock}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div>
                          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            Details
                          </p>

                          <div className="mb-2.5 grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2">
                            {(
                              [
                                ["Dentist", summaryData.IsDentist ? "Yes" : "No"],
                                ["Book dentist", summaryData.IsBookDentist ? "Yes" : "No"],
                                ["Appointment app", summaryData.hasAppointmentApp ? "Yes" : "No"],
                                ["Active", summaryData.status !== false ? "Yes" : "No"],
                              ] as const
                            ).map(([label, str]) => {
                              const on = str === "Yes";
                              return (
                                <div
                                  key={String(label)}
                                  className="flex min-w-0 items-center justify-between gap-2 rounded-md border border-slate-200/70 bg-white px-2 py-1.5 dark:border-slate-700/80 dark:bg-slate-900/60"
                                >
                                  <span className="min-w-0 truncate text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    {label}
                                  </span>
                                  <span
                                    className={`shrink-0 rounded px-1.5 py-px text-[10px] font-bold tabular-nums ${
                                      on
                                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/45 dark:text-emerald-200"
                                        : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                    }`}
                                  >
                                    {str}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          <div className="overflow-hidden rounded-lg border border-slate-100 bg-white dark:border-slate-800 dark:bg-slate-900/55">
                            <div className="divide-y divide-slate-100 dark:divide-slate-800">
                              {[
                                ["Studies", formatList(summaryData.studies)],
                                ["Specialities", formatList(summaryData.specialities)],
                                ["Languages", formatList(summaryData.languages)],
                                ["Address", summaryData.address],
                                [
                                  "Assigned treatments",
                                  Array.isArray(summaryData.assignedTreatmentIds)
                                    ? `${summaryData.assignedTreatmentIds.length} selected`
                                    : "—",
                                ],
                                ["Created", formatEmployeeSummaryDate(summaryData.createdAt)],
                                ["Updated", formatEmployeeSummaryDate(summaryData.updatedAt)],
                              ].map(([label, value]) => {
                                const str = String(value);
                                const isWide =
                                  label === "Address" ||
                                  label === "Studies" ||
                                  label === "Specialities" ||
                                  label === "Languages";
                                return (
                                  <div
                                    key={String(label)}
                                    className={`px-3 py-1.5 sm:px-3 sm:py-1.5 ${
                                      isWide ? "flex flex-col gap-1" : "flex flex-row items-baseline justify-between gap-4"
                                    }`}
                                  >
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                      {String(label)}
                                    </div>
                                    <div
                                      className={`min-w-0 text-sm font-medium leading-snug text-slate-800 dark:text-slate-100 ${
                                        isWide ? "" : "text-right"
                                      }`}
                                    >
                                      <span className="break-words">{str}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3.5 dark:border-slate-800 dark:bg-slate-800/35 sm:p-4">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            Calendar color
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            {calendarColorStr ? (
                              <>
                                <span
                                  className="h-10 w-10 shrink-0 rounded-lg border border-white/50 shadow-sm ring-1 ring-slate-900/8 dark:border-slate-600/50 dark:ring-white/8"
                                  style={{ backgroundColor: calendarColorStr }}
                                  title={calendarColorStr}
                                  aria-hidden
                                />
                                <div className="min-w-0 flex-1">
                                  {calendarColorLabel ? (
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                      {calendarColorLabel}
                                    </p>
                                  ) : null}
                                  <p className="mt-0.5 font-mono text-[10px] leading-relaxed text-slate-500 dark:text-slate-400">
                                    {calendarColorStr}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <span className="text-xs text-slate-500 dark:text-slate-400">Not set</span>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {!summaryLoading && !summaryData && (
                <div className="flex flex-col items-center justify-center gap-1.5 py-12 text-center">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">No profile data</p>
                  <p className="max-w-xs text-xs text-slate-500 dark:text-slate-400">
                    Something went wrong loading this employee. Try again or contact support.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
